/**
 * Build knowledge-service explore URLs from Scout tool calls (staff Observe).
 *
 * Corpus mapping mirrors packages/encamp-scout corpus-tools-loader TOOL_NAME_TO_CORPUS.
 * Filter keys mirror @encamp/knowledge-client CORPUS_SEARCH_FILTERS (kept local so Oak
 * does not depend on the knowledge packages).
 * Do not import Scout core into Oak.
 */

const DEFAULT_KNOWLEDGE_PUBLIC_URL = "http://localhost:4100";

/** Scout tool name → knowledge-service corpus id. */
const TOOL_NAME_TO_CORPUS: Record<string, string> = {
  search_knowledge: "helpkit",
  get_article: "helpkit",
  search_support: "support",
  get_support_article: "support",
  search_epa: "epa",
  get_epa_article: "epa",
  search_compliance: "compliance",
  exhaustive_search_compliance: "compliance",
  get_compliance_requirement: "compliance",
  search_regulations: "regulations",
  get_regulation: "regulations",
  browse_federal_regulations: "regulations",
  search_operations: "kb",
  get_operation: "kb",
  search_ca_regulations: "ca-regulations",
  get_ca_regulation: "ca-regulations",
  browse_ca_regulations: "ca-regulations",
  search_ny_regulations: "ny-regulations",
  get_ny_regulation: "ny-regulations",
  browse_ny_regulations: "ny-regulations",
};

/**
 * Per-corpus filter keys accepted on GET /corpora/:corpus/search.
 * Keep in sync with CORPUS_SEARCH_FILTERS in @encamp/knowledge-client.
 */
const CORPUS_FILTER_KEYS: Record<string, readonly string[]> = {
  regulations: ["titleFilter", "partFilter"],
  compliance: ["programArea", "jurisdiction", "ruleType"],
  helpkit: ["collection", "state", "type"],
  support: ["category"],
  epa: ["sourceKind", "sourceCollection"],
  kb: ["section", "kind", "excludeKinds"],
};

/** Scout corpus id → hierarchy slug (mirrors knowledge-core HIERARCHY_SCHEMES). */
const CORPUS_HIERARCHY_SLUG: Record<string, string> = {
  regulations: "cfr",
  "ca-regulations": "ccr",
  "ny-regulations": "ecl",
  helpkit: "academy",
};

const SEARCH_TOOLS = new Set([
  "search_knowledge",
  "search_support",
  "search_epa",
  "search_compliance",
  "exhaustive_search_compliance",
  "search_regulations",
  "search_operations",
  "search_ca_regulations",
  "search_ny_regulations",
]);

const GET_TOOLS = new Set([
  "get_article",
  "get_support_article",
  "get_epa_article",
  "get_compliance_requirement",
  "get_regulation",
  "get_operation",
  "get_ca_regulation",
  "get_ny_regulation",
]);

const BROWSE_TOOLS = new Set([
  "browse_federal_regulations",
  "browse_ca_regulations",
  "browse_ny_regulations",
]);

const QUERY_INPUT_KEYS = ["query", "q", "semanticSearchQuery", "search", "term", "topic"];

export function getKnowledgeCorpusForTool(toolName: string): string | null {
  return TOOL_NAME_TO_CORPUS[toolName] ?? null;
}

export function getKnowledgePublicBaseUrl(override?: string): string {
  const fromEnv =
    typeof import.meta !== "undefined" && import.meta.env?.VITE_KNOWLEDGE_PUBLIC_URL
      ? String(import.meta.env.VITE_KNOWLEDGE_PUBLIC_URL).trim()
      : "";
  // Explicit override (including "") wins over env so tests can force the default.
  const chosen = override !== undefined ? override.trim() : fromEnv;
  const raw = (chosen || DEFAULT_KNOWLEDGE_PUBLIC_URL).replace(/\/+$/, "");
  return raw.length > 0 ? raw : DEFAULT_KNOWLEDGE_PUBLIC_URL;
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return isPlainRecord(value) ? value : null;
}

function readString(value: unknown, key: string): string | null {
  const record = asRecord(value);
  if (record == null) {
    return null;
  }
  const raw = record[key];
  if (typeof raw !== "string") {
    return null;
  }
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function readFirstString(value: unknown, keys: string[]): string | null {
  for (const key of keys) {
    const v = readString(value, key);
    if (v !== null) {
      return v;
    }
  }
  return null;
}

function encodeFilterParamValue(value: unknown): string | string[] | null {
  if (value == null) {
    return null;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(Math.trunc(value));
  }
  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }
  if (Array.isArray(value)) {
    const parts = value
      .filter((item): item is string => typeof item === "string")
      .map((item) => item.trim())
      .filter((item) => item.length > 0);
    return parts.length > 0 ? parts : null;
  }
  return null;
}

/**
 * Collect explore-search filter query params from Scout tool input.
 * Reads flat filter keys and nested `filters` (exhaustive compliance).
 */
export function collectKnowledgeSearchFilterParams(
  corpus: string,
  toolInput: unknown
): Record<string, string | string[]> {
  const keys = CORPUS_FILTER_KEYS[corpus] ?? [];
  const out: Record<string, string | string[]> = {};
  const record = asRecord(toolInput);
  if (record == null || keys.length === 0) {
    return out;
  }

  const nested = asRecord(record.filters);

  for (const key of keys) {
    const raw = record[key] !== undefined ? record[key] : nested?.[key];
    const encoded = encodeFilterParamValue(raw);
    if (encoded != null) {
      out[key] = encoded;
    }
  }

  // exhaustive_search_compliance bridges onto compliance explore filters.
  if (corpus === "compliance") {
    if (out.ruleType == null && nested != null) {
      const encoded = encodeFilterParamValue(nested.type);
      if (encoded != null) {
        out.ruleType = encoded;
      }
    }
    if (out.jurisdiction == null && Array.isArray(record.jurisdictions)) {
      const first = record.jurisdictions.find(
        (item): item is string => typeof item === "string" && item.trim().length > 0
      );
      if (first != null) {
        out.jurisdiction = first.trim();
      }
    }
  }

  return out;
}

function appendSearchParams(url: URL, params: Record<string, string | string[]>): void {
  for (const [key, value] of Object.entries(params)) {
    if (Array.isArray(value)) {
      for (const item of value) {
        url.searchParams.append(key, item);
      }
    } else {
      url.searchParams.set(key, value);
    }
  }
}

function corporaPath(corpus: string, ...segments: string[]): string {
  const parts = [corpus, ...segments].map((segment) => encodeURIComponent(segment));
  return `/corpora/${parts.join("/")}`;
}

/**
 * Resolve a knowledge node id for get-tool Observe links.
 * Prefer the caller's nodeId; fall back to remote get output (`id` / `nodeId`)
 * so citation-only lookups (e.g. get_regulation) still deep-link after success.
 */
function resolveGetToolNodeId(toolInput: unknown, toolOutput: unknown): string | null {
  return (
    readString(toolInput, "nodeId") ??
    readFirstString(toolOutput, ["nodeId", "id"]) ??
    readString(toolInput, "id")
  );
}

/**
 * Build an absolute knowledge-service explore href for a Scout tool call.
 * Returns null when the tool is not knowledge-backed or required fields are missing.
 */
export function buildKnowledgeServiceHref(input: {
  toolName: string;
  toolInput?: unknown;
  toolOutput?: unknown;
  baseUrl?: string;
}): string | null {
  const corpus = getKnowledgeCorpusForTool(input.toolName);
  if (corpus == null) {
    return null;
  }

  const base = getKnowledgePublicBaseUrl(input.baseUrl);
  const toolInput = input.toolInput;

  if (SEARCH_TOOLS.has(input.toolName)) {
    const query = readFirstString(toolInput, QUERY_INPUT_KEYS) ?? "";
    const path = corporaPath(corpus, "search");
    const url = new URL(path, `${base}/`);
    url.searchParams.set("q", query);
    appendSearchParams(url, collectKnowledgeSearchFilterParams(corpus, toolInput));
    return url.toString();
  }

  if (GET_TOOLS.has(input.toolName)) {
    const nodeId = resolveGetToolNodeId(toolInput, input.toolOutput);
    if (nodeId == null) {
      return null;
    }
    return `${base}${corporaPath(corpus, "nodes", nodeId)}`;
  }

  if (BROWSE_TOOLS.has(input.toolName)) {
    const nodeId = readString(toolInput, "nodeId");
    const slug = CORPUS_HIERARCHY_SLUG[corpus];
    if (nodeId == null) {
      if (slug != null) {
        return `${base}/corpus/${encodeURIComponent(slug)}`;
      }
      return `${base}${corporaPath(corpus, "browse")}`;
    }
    return `${base}${corporaPath(corpus, "nodes", nodeId)}`;
  }

  return null;
}
